import { useSyncExternalStore } from 'react'
import { HEADER_CIRCLE } from '../../../../components/site-header/sizes'
import { useCopy } from '../../content'
import { applyTheme, notifyTheme, subscribeTheme, THEME_STORAGE_KEY, type Theme } from '../../theme/theme'

function SunIcon() {
  return (
    <svg
      data-theme-icon="sun"
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <circle cx="8" cy="8" r="2.75" />
      <path d="M8 1.5V3M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.06 1.06M11.54 11.54l1.06 1.06M3.4 12.6l1.06-1.06M11.54 4.46l1.06-1.06" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg
      data-theme-icon="moon"
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    >
      <path d="M13.5 9.5A5.5 5.5 0 1 1 7 3a4.8 4.8 0 0 0 6.5 6.5Z" />
    </svg>
  )
}

/** The bar's paper coin (cream on the forest-dark bar, both themes); the mobile-menu body passes its own. */
export const BAR_COIN = 'border-brand-cream/20 bg-brand-cream text-brand-forest-dark'

/** Client snapshot: the root class is the one source of truth (the boot script sets it before hydration). */
const readIsDark = () => document.documentElement.classList.contains('dark')
/** Server snapshot: the prerendered HTML cannot know the visitor's theme. */
const unknownOnServer = () => null

/**
 * Light/dark switch in the header. The site is light unless the visitor picks
 * dark here; the choice is stored in localStorage and re-applied before first
 * paint by the boot script (theme/inlineScript.ts).
 *
 * Hydration contract: the server snapshot is `null`, so the prerendered button
 * and the hydrating render carry no `aria-pressed` and read nothing from
 * `document`; React then re-renders with the root class. Both icons are always
 * in the markup and vc2.css shows the one for the root's class (sun in light,
 * moon in dark), so the right icon paints first time. The label is fixed and
 * names what "pressed" asserts, the dark theme ("Tema scuro" / "Dark theme",
 * APG toggle-button pattern); `aria-pressed` (pressed = dark) carries the
 * state.
 *
 * The bar and the open mobile menu each mount a toggle: they share the theme
 * store (theme.ts), so a switch in one updates the other's `aria-pressed`.
 *
 * `className` REPLACES the colour set (border, background, text) rather than
 * adding to it — two Tailwind colours on one element resolve by stylesheet
 * order, not by class order. Default: the bar's paper coin.
 */
export function ThemeToggle({ className = BAR_COIN }: { className?: string }) {
  const copy = useCopy()
  const isDark = useSyncExternalStore<boolean | null>(subscribeTheme, readIsDark, unknownOnServer)

  const toggle = () => {
    const root = document.documentElement
    const next: Theme = root.classList.contains('dark') ? 'light' : 'dark'
    applyTheme(root, document.head.querySelector('meta[name="theme-color"]'), next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Storage blocked: the theme still changes, it just is not remembered.
    }
    notifyTheme()
  }

  return (
    <button
      type="button"
      data-theme-toggle
      aria-label={copy.a11y.themeToggle}
      aria-pressed={isDark ?? undefined}
      onClick={toggle}
      className={`${HEADER_CIRCLE} ${className} transition-opacity hover:opacity-90`}
    >
      <SunIcon />
      <MoonIcon />
    </button>
  )
}
